import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Chip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Stack,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridSortModel
} from '@mui/x-data-grid';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
    Home as HomeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { auditLogAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { UserRole } from '../../types';
import { formatDate, formatTimeString } from '../../utils/dateUtils';

// Define interfaces for audit log data
interface AuditLog {
    id: string;
    userId: string | null;
    userEmail: string | null;
    userFullName: string | null;
    userRole: string | null;
    entityType: string;
    entityId: string | null;
    action: string;
    description: string;
    previousValues: Record<string, any> | null;
    newValues: Record<string, any> | null;
    changedFields: string[] | null;
    ipAddress: string | null;
    userAgent: string | null;
    requestId: string | null;
    severity: string;
    category: string | null;
    metadata: Record<string, any> | null;
    isSystemAction: boolean;
    source: string | null;
    createdAt: string;
}

interface PaginatedResponse {
    data: AuditLog[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

const AuditLogsPage: React.FC = () => {
    const { user: currentUser } = useAuth();
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination states
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(50);
    const [totalRows, setTotalRows] = useState<number>(0);

    // Sorting state
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'createdAt', sort: 'desc' }]);

    // Filter states
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
    const [actionFilter, setActionFilter] = useState<string>('');
    const [severityFilter, setSeverityFilter] = useState<string>('');
    const [entityTypeFilter, setEntityTypeFilter] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [userFilter, setUserFilter] = useState<string>('');

    // Detail dialog state
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);

    // Debounce search text
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
            setPage(0); // Reset to first page on search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchText]);

    // Fetch audit logs with pagination and filters
    const fetchAuditLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: page + 1, // API uses 1-based pagination
                limit: pageSize
            };

            // Apply search
            if (debouncedSearchText) {
                params.search = debouncedSearchText;
            }

            // Apply filters
            if (actionFilter) params.action = actionFilter;
            if (severityFilter) params.severity = severityFilter;
            if (entityTypeFilter) params.entityType = entityTypeFilter;
            if (categoryFilter) params.category = categoryFilter;
            if (userFilter) params.userId = userFilter;

            // Apply sorting
            if (sortModel.length > 0) {
                params.sortBy = sortModel[0].field;
                params.sortOrder = sortModel[0].sort?.toUpperCase() || 'DESC';
            }

            const response = await auditLogAPI.getAuditLogs(params);
            const result: PaginatedResponse = response.data;

            setAuditLogs(result.data);
            setTotalRows(result.pagination.total);
            setError(null);
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            setError('Failed to load audit logs. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, debouncedSearchText, actionFilter, severityFilter, entityTypeFilter, categoryFilter, userFilter, sortModel]);

    useEffect(() => {
        fetchAuditLogs();
    }, [fetchAuditLogs]);

    // Handle pagination model change
    const handlePaginationModelChange = (newModel: { page: number; pageSize: number }) => {
        setPage(newModel.page);
        setPageSize(newModel.pageSize);
    };

    // Get severity color
    const getSeverityColor = (severity: string) => {
        switch (severity.toLowerCase()) {
            case 'critical': return 'error';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'default';
            default: return 'default';
        }
    };

    // Format action for display
    const formatAction = (action: string) => {
        if (!action) return '';
        return action
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Format severity for display
    const formatSeverity = (severity: string) => {
        if (!severity) return '';
        return severity.charAt(0).toUpperCase() + severity.slice(1).toLowerCase();
    };

    // Format entity type for display
    const formatEntityType = (entityType: string) => {
        return entityType.replace(/([A-Z])/g, ' $1').trim();
    };

    // Handle sort model change
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
        setPage(0); // Reset to first page on sort
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchText('');
        setDebouncedSearchText('');
        setActionFilter('');
        setSeverityFilter('');
        setEntityTypeFilter('');
        setCategoryFilter('');
        setUserFilter('');
        setSortModel([{ field: 'createdAt', sort: 'desc' }]);
        setPage(0);
    };

    // Get unique values for filters
    const getUniqueActions = () => {
        const actions = auditLogs.map(log => log.action);
        return [...new Set(actions)].sort();
    };

    const getUniqueSeverities = () => {
        const severities = auditLogs.map(log => log.severity);
        return [...new Set(severities)].sort();
    };

    const getUniqueEntityTypes = () => {
        const types = auditLogs.map(log => log.entityType);
        return [...new Set(types)].sort();
    };

    const getUniqueCategories = () => {
        const categories = auditLogs.map(log => log.category).filter(c => c);
        return [...new Set(categories)].sort();
    };

    // Handle view details
    const handleViewDetails = (log: AuditLog) => {
        setSelectedLog(log);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setSelectedLog(null);
    };

    // Define columns for DataGrid
    const columns: GridColDef[] = [
        {
            field: 'createdAt',
            headerName: 'Date/Time',
            width: 180,
            renderCell: (params: GridRenderCellParams) => {
                if (!params.value) return 'N/A';
                try {
                    const date = new Date(params.value);
                    return (
                        <Box>
                            <Typography variant="body2">
                                {date.toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {date.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                })}
                            </Typography>
                        </Box>
                    );
                } catch (error) {
                    return 'Invalid Date';
                }
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2">
                    {formatAction(params.value || '')}
                </Typography>
            ),
        },
        {
            field: 'severity',
            headerName: 'Severity',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2">
                    {formatSeverity(params.value || '')}
                </Typography>
            ),
        },
        {
            field: 'userFullName',
            headerName: 'User',
            width: 200,
            renderCell: (params: GridRenderCellParams) => {
                const { userFullName, userEmail, isSystemAction } = params.row;
                if (isSystemAction) {
                    return (
                        <Box>
                            <Typography variant="body2" fontWeight="bold">
                                SYSTEM
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Automated Action
                            </Typography>
                        </Box>
                    );
                }
                return (
                    <Box>
                        <Typography variant="body2">
                            {userFullName || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {userEmail || 'N/A'}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'entityType',
            headerName: 'Entity Type',
            width: 150,
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2">
                    {formatEntityType(params.value || '')}
                </Typography>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 300,
            flex: 1,
        },
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <AdminAccessControl allowedRoles={[UserRole.SUPER_ADMIN, UserRole.ADMIN]}>
                {/* Breadcrumbs */}
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                    sx={{ mb: 3 }}
                >
                    <RouterLink
                        className='user-link'
                        style={{ display: 'flex', alignItems: 'center' }}
                        to="/dashboard"
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Dashboard
                    </RouterLink>
                    <RouterLink
                        className='user-link'
                        style={{ display: 'flex', alignItems: 'center' }}
                        to="/reports"
                    >
                        Reports
                    </RouterLink>
                    <Typography color="text.primary">Audit Logs</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        Audit Logs
                    </Typography>
                </Box>

                {/* Error message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Main content */}
                <Paper sx={{ p: 3 }}>
                    {/* Filters */}
                    <Stack spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                placeholder="Search logs..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                size="small"
                                sx={{ minWidth: 250 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Action</InputLabel>
                                <Select
                                    value={actionFilter}
                                    onChange={(e) => setActionFilter(e.target.value)}
                                    label="Action"
                                >
                                    <MenuItem value="">All Actions</MenuItem>
                                    {getUniqueActions().map((action) => (
                                        <MenuItem key={action} value={action}>
                                            {formatAction(action)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Severity</InputLabel>
                                <Select
                                    value={severityFilter}
                                    onChange={(e) => setSeverityFilter(e.target.value)}
                                    label="Severity"
                                >
                                    <MenuItem value="">All Severities</MenuItem>
                                    {getUniqueSeverities().map((severity) => (
                                        <MenuItem key={severity} value={severity}>
                                            {formatSeverity(severity)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Entity Type</InputLabel>
                                <Select
                                    value={entityTypeFilter}
                                    onChange={(e) => setEntityTypeFilter(e.target.value)}
                                    label="Entity Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {getUniqueEntityTypes().map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {formatEntityType(type)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {getUniqueCategories().length > 0 && (
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                        label="Category"
                                    >
                                        <MenuItem value="">All Categories</MenuItem>
                                        {getUniqueCategories().map((category) => (
                                            <MenuItem key={category || 'unknown'} value={category || ''}>
                                                {category || 'Unknown'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <Button
                                variant="outlined"
                                onClick={clearFilters}
                                startIcon={<ClearIcon />}
                                size="small"
                            >
                                Clear Filters
                            </Button>
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                            Showing {auditLogs.length} of {totalRows} total audit log entries
                        </Typography>
                    </Stack>

                    {loading && page === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : auditLogs.length === 0 ? (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography variant="body1">
                                No audit logs found matching the current filters.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ height: 700, width: '100%' }}>
                            <DataGrid
                                rows={auditLogs}
                                columns={columns}
                                getRowId={(row) => row.id}
                                pagination
                                paginationMode="server"
                                sortingMode="server"
                                rowCount={totalRows}
                                paginationModel={{ page, pageSize }}
                                onPaginationModelChange={handlePaginationModelChange}
                                sortModel={sortModel}
                                onSortModelChange={handleSortModelChange}
                                pageSizeOptions={[25, 50, 100]}
                                checkboxSelection={false}
                                disableRowSelectionOnClick
                                loading={loading}
                                onRowClick={(params) => handleViewDetails(params.row)}
                                getRowHeight={() => 'auto'}
                                sx={{
                                    '& .MuiDataGrid-cell': {
                                        py: 1,
                                    },
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Paper>

                {/* Detail Dialog */}
                <Dialog
                    open={detailDialogOpen}
                    onClose={handleCloseDetailDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Audit Log Details</DialogTitle>
                    <DialogContent>
                        {selectedLog && (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Date/Time
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(selectedLog.createdAt).toLocaleString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            second: '2-digit'
                                        })}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Action
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatAction(selectedLog.action || '')}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Severity
                                    </Typography>
                                    <Typography variant="body1">
                                        {formatSeverity(selectedLog.severity || '')}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Description
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedLog.description}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        User
                                    </Typography>
                                    {selectedLog.isSystemAction ? (
                                        <Typography variant="body1" fontWeight="bold">
                                            SYSTEM (Automated Action)
                                        </Typography>
                                    ) : (
                                        <Box>
                                            <Typography variant="body1">
                                                {selectedLog.userFullName || 'Unknown User'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {selectedLog.userEmail || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Role: {selectedLog.userRole?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ') || 'N/A'}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Entity
                                    </Typography>
                                    <Typography variant="body1">
                                        Type: {formatEntityType(selectedLog.entityType || '')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ID: {selectedLog.entityId || 'N/A'}
                                    </Typography>
                                </Box>

                                {selectedLog.changedFields && selectedLog.changedFields.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Changed Fields
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {selectedLog.changedFields.map((field) => (
                                                <Chip key={field} label={field} size="small" />
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                {selectedLog.previousValues && Object.keys(selectedLog.previousValues).length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Previous Values
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <pre style={{ margin: 0, overflow: 'auto' }}>
                                                {JSON.stringify(selectedLog.previousValues, null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                {selectedLog.newValues && Object.keys(selectedLog.newValues).length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            New Values
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <pre style={{ margin: 0, overflow: 'auto' }}>
                                                {JSON.stringify(selectedLog.newValues, null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                {selectedLog.category && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Category
                                        </Typography>
                                        <Typography variant="body1">
                                            {selectedLog.category}
                                        </Typography>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Technical Details
                                    </Typography>
                                    <Typography variant="body2">
                                        IP Address: {selectedLog.ipAddress || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                        Source: {selectedLog.source || 'N/A'}
                                    </Typography>
                                    {selectedLog.requestId && (
                                        <Typography variant="body2" color="text.secondary">
                                            Request ID: {selectedLog.requestId}
                                        </Typography>
                                    )}
                                </Box>

                                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Additional Metadata
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                                            <pre style={{ margin: 0, overflow: 'auto' }}>
                                                {JSON.stringify(selectedLog.metadata, null, 2)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDetailDialog}>Close</Button>
                    </DialogActions>
                </Dialog>
            </AdminAccessControl>
        </Container>
    );
};

export default AuditLogsPage;
